import React, { useEffect } from 'react'
import tw, { styled } from 'twin.macro'

import { convertQueryParamStringToObject } from 'libs/convertQueryParamStringToObject'
import { toast } from 'libs/toast'
import useSaveUserActivityLog, { ACTION_NAME } from 'hooks/useSaveUserActivityLog'
import useAxios from 'hooks/useAxios'
import { useLocation } from 'react-router-dom'
import { oauthDomain } from 'domains/authentication/oauth'

export default function IntercomOauthHandler() {
  const { fetch: accessTokenTradeAPI } = useAxios({
    url: 'intercom/access-token-trade/',
    method: 'post',
    skip: true,
  })
  const { fetch: toggleCustomerSyncAPI } = useAxios({
    url: 'intercom/trigger-sync-customer/',
    method: 'patch',
    skip: true,
  })
  const location = useLocation()
  const saveUserActivityLog = useSaveUserActivityLog()

  useEffect(() => {
    if (!localStorage.getItem('simplesatToken')) {
      toast.error(`Please logged in to Simplesat account.`)
      const errorMessage = 'Please, logged in to Simplesat account and try again.'
      window.location.replace(
        'https://app.intercom.com/appstore/redirect?error_message=' +
          encodeURIComponent(errorMessage)
      )
    } else {
      const { state: nonce, code } = convertQueryParamStringToObject(location.search)
      const { workspaceToken } = oauthDomain.getAppState(nonce)
      const requestPayload = { code, workspace_token: workspaceToken }

      const headers = {
        'X-Workspace-Token': workspaceToken,
      }

      if (oauthDomain.isNonceValid(nonce)) {
        accessTokenTradeAPI({
          data: requestPayload,
        })
          .then((response) => {
            toast.success(
              "Woohoo! Simplesat is now connected with Intercom, please we're directing you back to Intercom."
            )
            saveUserActivityLog({
              action: ACTION_NAME.authenticated_intercom,
              target: 'intercom_credential',
              requestBody: requestPayload,
              statusCode: response.status,
              headers: headers,
            })

            const customerSyncData = {
              enable: true,
              integration: 'customer_sync',
            }
            // Enable customer sync (kicks off the initial sync) and wait for it to
            // settle BEFORE redirecting — window.location.replace is a full page
            // navigation that would abort the in-flight request.
            return toggleCustomerSyncAPI({ data: customerSyncData, headers: headers })
              .then(() => {
                saveUserActivityLog({
                  action: ACTION_NAME.connected_intercom,
                  target: 'intercom_credential',
                  requestBody: customerSyncData,
                  statusCode: 200,
                  headers: headers,
                })
              })
              .catch((error) => {
                saveUserActivityLog({
                  action: ACTION_NAME.connected_intercom,
                  target: 'intercom_credential',
                  requestBody: customerSyncData,
                  errorResponseBody: customerSyncData,
                  statusCode: error.response?.status,
                  headers: headers,
                })
              })
              .then(() => {
                window.location.replace(
                  'https://app.intercom.com/appstore/redirect?install_success=true'
                )
              })
          })
          .catch((error) => {
            toast.error('Unable to authenticate. Please try again.')
            saveUserActivityLog({
              action: ACTION_NAME.authenticated_intercom,
              target: 'intercom_credential',
              requestBody: requestPayload,
              statusCode: error.status,
              headers: headers,
            })

            const errorMessage =
              "There're some error from our server, Please try again later or contact Simplesat support."
            window.location.replace(
              'https://app.intercom.com/appstore/redirect?error_message=' +
                encodeURIComponent(errorMessage)
            )
          })
      } else {
        toast.error(
          `There was an error authenticating. If you can't get it working after a few tries please contact the Simplesat team to assist.`
        )
        window.location.replace('/')

        saveUserActivityLog({
          action: ACTION_NAME.authenticated_intercom,
          target: 'intercom_credential',
          requestBody: {},
          errorResponseBody: {},
          statusCode: 400,
        })
      }

      oauthDomain.deleteNonce(nonce)
      oauthDomain.cleanupNonces()
    }
  }, [location.search, accessTokenTradeAPI, toggleCustomerSyncAPI, saveUserActivityLog])

  return (
    <Container>
      <Header>
        <img
          src="https://cdn.simplesat.io/images/simplesat-logomark-green-small.png"
          alt="simplesat-logo"
        />
      </Header>
      <Body>
        <ContentWrapper>
          <img alt="loader" src="https://cdn.simplesat.io/intercom/loader-intercom-oauth.gif" />
          <p className="text-center">Linking your Intercom and Simplesat accounts</p>
        </ContentWrapper>
      </Body>
    </Container>
  )
}

const Container = styled.div`
  ${tw`flex flex-col h-full`}
`

const Header = styled.header`
  ${tw`bg-white flex justify-center items-center py-3`}
  height: auto;
  min-height: 69px;

  img {
    width: 45px;
  }

  @media (min-width: 768px) {
    min-height: 79px;

    img {
      width: 55px;
    }
  }
`

const Body = styled.section`
  ${tw`flex-grow flex justify-center items-center py-0 px-4`}
`

const ContentWrapper = styled.div`
  ${tw`bg-white flex flex-col items-center px-3 shadow-md`}

  border-radius: 10px;
  padding: 40px 10px;

  h2 {
    font-size: 3.75rem;
    line-height: 1;
    margin: 0;
  }

  h3 {
    font-size: 1.25rem;
  }

  @media (min-width: 768px) {
    min-width: 700px;
    padding-top: 7.75rem;
    padding-bottom: 7.75rem;

    h2 {
      font-size: 5rem;
      line-height: 1;
    }

    h3 {
      font-size: 1.75rem;
    }

    p {
      font-size: 1.25rem;
    }
  }

  @media (min-width: 1200px) {
    min-width: 962px;
  }
`



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/pages/OauthHandler/intercom.js